import { createContext, useContext, useMemo } from 'react';
import { defineMessages } from 'react-intl';
import { BehaviorSubject, debounce, interval, map, merge } from 'rxjs';
import { useEventListener, useIntl, useObservableValue } from '../hooks';
import { HIGH_LATENCY_THRESHOLD, useHeartbeats } from './HeartbeatsProvider';

interface NetworkConnectionStatusState {
  text?: string;
}

const messages = defineMessages({
  noConnection: {
    id: 'providers.noConnection',
    defaultMessage: 'No Connection',
  },
  unstableConnection: {
    id: 'providers.unstableConnection',
    defaultMessage: 'Unstable Connection',
  },
});

const NetworkConnectionStatusContext = createContext<NetworkConnectionStatusState | undefined>({});

export const useNetworkConnectionStatus = () => {
  const context = useContext(NetworkConnectionStatusContext);
  if (context === undefined) {
    throw new Error('useNetworkConnectionStatus must be used within a NetworkConnectionStatusProvider');
  }
  return context;
};

export function NetworkConnectionStatusProvider({
  children,
  latencyThreshold = HIGH_LATENCY_THRESHOLD,
}: React.PropsWithChildren<{ latencyThreshold?: number }>) {
  const { latency: latencyObs } = useHeartbeats();
  const networkStatusSubject = useMemo(() => new BehaviorSubject<boolean>(navigator.onLine), []);
  useEventListener('online', () => {
    networkStatusSubject.next(true);
  });
  useEventListener('offline', () => {
    networkStatusSubject.next(false);
  });
  const intl = useIntl();

  const text = useObservableValue(
    () =>
      merge(
        networkStatusSubject.pipe(
          map(connected => (connected ? undefined : intl.formatMessage(messages.noConnection)))
        ),
        latencyObs.pipe(
          map(latency =>
            latency === Infinity
              ? intl.formatMessage(messages.noConnection)
              : latency >= latencyThreshold
                ? intl.formatMessage(messages.unstableConnection)
                : undefined
          )
        )
      ).pipe(debounce(() => interval(2000))),
    [networkStatusSubject, latencyObs, intl, latencyThreshold],
    undefined
  );

  const value = useMemo<NetworkConnectionStatusState>(() => ({ text }), [text]);

  return <NetworkConnectionStatusContext.Provider value={value}>{children}</NetworkConnectionStatusContext.Provider>;
}
